import React from 'react';
import { Result } from 'antd';

import { useAuthStore } from '@/stores/auth.store';

interface RequireRoleProps {
  /** Roles that are allowed to see this content */
  roles: string[];
  /** Content to render if the user has the required role */
  children: React.ReactNode;
  /** If true, renders nothing instead of an access denied message */
  silent?: boolean;
}

/**
 * RBAC guard component.
 * - Wraps content that should only be visible to users with specific roles.
 * - `silent` mode: hides content without showing "no permission" message (for buttons, menu items).
 * - Default mode: shows a "no permission" page (for full-page route guards).
 */
export const RequireRole: React.FC<RequireRoleProps> = ({ roles, children, silent = false }) => {
  const { user } = useAuthStore();
  const userRole = user?.role?.toUpperCase() ?? '';

  const normalizedRoles = roles.map((r) => r.toUpperCase());

  if (normalizedRoles.includes(userRole)) {
    return <>{children}</>;
  }

  if (silent) {
    return null;
  }

  return (
    <Result
      status="403"
      title="无权限"
      subTitle="您没有权限访问此页面，请联系管理员。"
    />
  );
};

/**
 * Hook to check if current user has a specific role.
 */
export function useHasRole(...roles: string[]): boolean {
  const { user } = useAuthStore();
  const userRole = user?.role?.toUpperCase() ?? '';
  return roles.some((r) => r.toUpperCase() === userRole);
}
